import { useCallback, useState } from "react";
import { useBookingContext } from "@/contexts/BookingContext";
import { api } from "@/services/api";
import type { AvailabilityCampsite } from "@/types/api";
import { logError } from "@/utils/logError";

interface UseAvailabilityReturn {
  loading: boolean;
  availableCampsites: AvailabilityCampsite[];
  checkAvailability: (params: {
    url: string;
    dateFrom: string;
    dateUntil: string;
    currencyCode?: string;
    bookingId?: string;
  }) => Promise<AvailabilityCampsite[]>;
}

export function useAvailability(): UseAvailabilityReturn {
  const { state } = useBookingContext();
  const [loading, setLoading] = useState(false);

  const checkAvailability = useCallback(
    async (params: {
      url: string;
      dateFrom: string;
      dateUntil: string;
      currencyCode?: string;
      bookingId?: string;
    }) => {
      if (Object.keys(state.selectedPersons).length === 0) {
        return [];
      }

      setLoading(true);

      try {
        const response = await api.checkAvailability(
          params.url,
          {
            date_from: params.dateFrom,
            date_until: params.dateUntil,
            currency_alt: params.currencyCode,
            booking_id: params.bookingId,
          },
          Object.entries(state.selectedPersons).map(([id, amount]) => ({
            id: parseInt(id, 10),
            amount,
          })),
        );

        setLoading(false);
        return response.campsites;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logError(error);
        setLoading(false);
        throw error;
      }
    },
    [state.selectedPersons],
  );

  return {
    loading,
    availableCampsites: [],
    checkAvailability,
  };
}
